import { Controller } from "@hotwired/stimulus"

// −/+ buttons around a quantity field. A step goes through the field's own min / max and fires `input` and
// `change` as typing does, so whatever listens to the field (summary, totals, autosave) follows.
// The buttons are named after the field, "__FIELD__" standing for its accessible name in the label values.
// Connects to data-controller="erp--quantity-stepper"
export default class extends Controller {
  static targets = ["input", "decrement", "increment"]
  static values = { decrementLabel: String, incrementLabel: String }

  connect() {
    this.#renderLabels()
    // The editor renames a size field when it reuses the row for another size
    this.observer = new MutationObserver(() => this.#renderLabels())
    if (this.hasInputTarget) this.observer.observe(this.inputTarget, { attributes: true, attributeFilter: ["aria-label"] })
  }

  disconnect() {
    this.observer?.disconnect()
    this.observer = null
  }

  decrement() {
    this.#step(-1)
  }

  increment() {
    this.#step(1)
  }

  #step(direction) {
    if (!this.hasInputTarget || this.inputTarget.readOnly) return

    const input = this.inputTarget

    input.value = this.#clamp(input, (parseInt(input.value, 10) || 0) + direction)
    input.dispatchEvent(new Event("input", { bubbles: true }))
    input.dispatchEvent(new Event("change", { bubbles: true }))
  }

  #clamp(input, value) {
    const min = input.min === "" ? -Infinity : Number(input.min)
    const max = input.max === "" ? Infinity : Number(input.max)
    return Math.min(Math.max(value, min), max)
  }

  #renderLabels() {
    const field = this.hasInputTarget ? this.inputTarget.getAttribute("aria-label") : null
    if (!field) return

    this.#label(this.decrementTargets, this.decrementLabelValue, field)
    this.#label(this.incrementTargets, this.incrementLabelValue, field)
  }

  #label(buttons, template, field) {
    if (template) buttons.forEach(button => button.setAttribute("aria-label", template.replace("__FIELD__", field)))
  }
}
